import { useTheme } from '@emotion/react';
import CheckCircleOutlined from '@mui/icons-material/CheckCircleOutlined';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import { KeyboardEvent, useEffect, useRef, useState } from 'react';
import { toast as libToast } from 'react-hot-toast';
import useClickOutside from '../../utils/useClickOutside/useClickOutside';
import Button from '../Button/Button';
import { Variant } from '../Button/Button.types';
import {
  ButtonWrapper,
  CloseButton,
  IconAndMessage,
  Message,
  MessageContainer,
  Subtext,
  ToastElementContainer,
} from './Toast.styles';
import { ToastElementProps, ToastTypes } from './Toast.types';

/**
 * This component is rendered using the `toast` function exported from 'components/Toast/Toast' - it should not be used individually (there are many required props created by react-hot-toast)
 */
const ToastElement: React.FC<ToastElementProps> = ({
  label,
  buttonLabel,
  onButtonClick,
  toastType,
  id,
  startPause,
  endPause,
  triggerFocus = false,
  visible,
  subtext,
}) => {
  const theme = useTheme();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [previousFocus] = useState(document.activeElement);
  const dismissThisToast = () => {
    libToast.dismiss(id);
  };

  useClickOutside(containerRef as React.RefObject<HTMLElement>, visible, dismissThisToast);

  const unpause = () => {
    // does not stop pause if there is a cta or if elements within the toast have focus
    if (
      (buttonLabel && onButtonClick) ||
      document.activeElement === buttonRef.current ||
      document.activeElement === closeButtonRef.current
    ) {
      return;
    }
    if (endPause) endPause();
  };

  const pause = () => {
    if (startPause) startPause();
  };

  // locks the focus within the dialog
  const toggleFocus = (e: KeyboardEvent) => {
    e.preventDefault();
    if (e.target === closeButtonRef.current) {
      if (buttonRef.current) buttonRef.current.focus();
    } else {
      if (closeButtonRef.current) closeButtonRef.current.focus();
    }
  };

  const handleFocus = () => {
    pause();
  };

  const handleBlur = () => {
    unpause();
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    switch (e.key) {
      case 'Escape':
        dismissThisToast();
        break;
      case 'Tab':
        toggleFocus(e);
        break;
    }
  };

  useEffect(() => {
    if (visible) {
      // if there is a CTA, focus CTA
      if (buttonRef.current !== null) buttonRef.current.focus();
      // if there is no CTA + triggerFocus prop is true, focus close button
      if (closeButtonRef.current && triggerFocus && buttonRef.current === null) closeButtonRef.current.focus();
      // when the ToastElement is no longer visible, refocus the element that was focused when it was opened
    } else {
      if (previousFocus) (previousFocus as HTMLElement).focus();
    }
  }, [visible]);

  const renderIcon = () => {
    switch (toastType) {
      case ToastTypes.Info:
        return <InfoOutlined htmlColor={theme.colors.blue} />;
      case ToastTypes.Error:
        return <ErrorOutlineOutlinedIcon htmlColor={theme.colors.red} />;
      case ToastTypes.Success:
      default:
        return <CheckCircleOutlined htmlColor={theme.colors.green} />;
    }
  };

  return (
    <ToastElementContainer
      type={toastType}
      onKeyDown={handleKeyDown}
      data-testid='ToastElement'
      onMouseEnter={pause}
      onMouseLeave={unpause}
      ref={containerRef}
      role={buttonLabel && onButtonClick ? 'alertdialog' : 'alert'}
      aria-describedby={`${id}-message`}
      visible={visible}
    >
      <IconAndMessage>
        {renderIcon()}
        <MessageContainer>
          <Message id={`${id}-message`}>{label}</Message>
          {subtext && <Subtext>{subtext}</Subtext>}
          {onButtonClick && buttonLabel ? (
            <ButtonWrapper>
              <Button
                variant={Variant.Text}
                label={buttonLabel}
                ref={buttonRef}
                onFocus={handleFocus}
                onBlur={handleBlur}
                onClick={onButtonClick}
              />
            </ButtonWrapper>
          ) : (
            <></>
          )}
        </MessageContainer>
      </IconAndMessage>
      <CloseButton
        ref={closeButtonRef}
        onClick={dismissThisToast}
        onFocus={handleFocus}
        onBlur={handleBlur}
        aria-label='close'
      >
        <CloseOutlinedIcon fontSize='inherit' color='inherit' />
      </CloseButton>
    </ToastElementContainer>
  );
};

export default ToastElement;
